import { useContext, Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Container } from 'reactstrap';
import { Button } from 'reactstrap';
import { AuthenticationContext } from '../../../msal/AuthenticationContext';
import { NotificationContext } from '../../../context/NotificationContext';
import { TenantContext } from '../../../context/TenantContext';
import { IqMessengerConnections } from './IqMessengerConnections';
import { ModalConnectionAddOrUpdate } from './ModalConnectionAddOrUpdate';
import * as Misc from '../../../utils/misc';
import * as apiCalls from '../../../logic/apiCalls';
import { ButtonWithDisabledTooltip } from '../../../components/ButtonWithDisabledTooltip';
import { Alert } from 'react-bootstrap';

// The maximum allowed number of connections per tenant.
const maximumNumberOfConnections = 500;

class IqMessengerSettingsInternal extends Component {

    state = {
        isLoading: true,
        connections: [],
        connectionToDelete: {},
        connectionToUpdate: {},
        isConfirmDeleteModalOpen: false,
        isConnectionModalOpen: false,
        isConnectionModalUpdate: false,
    }

    async componentDidMount() {
        this.getConnections();
    }

    showConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: true,
        });
    };

    hideConfirmDeleteModal = () => {
        this.setState({
            isConfirmDeleteModalOpen: false,
        });
    };

    showAddConnectionModal = () => {
        this.setState({
            isConnectionModalUpdate: false,
            isConnectionModalOpen: true,
        });
    };

    showUpdateConnectionModal = () => {
        this.setState({
            isConnectionModalUpdate: true,
            isConnectionModalOpen: true,
        });
    };

    hideConnectionModal = () => {
        this.referenceIdToAdd = '';
        this.setState({
            isConnectionModalOpen: false,
        });

        this.setState({
            redirectToMainConnectionPage: true,
        });
    }
    
    getConnections = async () => {
        try {
            console.log("[getConnections] Invoking the back-end API to get all IQ messenger connections of this tenant.");

            const connections = await apiCalls.getIqMessengerConnections(this.props.authenticationContext);

            this.setState({
                connections: connections,
            });               
        }
        catch (err) {
            console.log(`[getConnections] Error retrieving the IQ messenger connections: ${err}`);
            this.props.notificationContext.setCommunicationFailureNotification();               
        }
        finally {
            this.setState({
                isLoading: false,
            });
        }
    }

    handleConnectionDelete = (connection) => {
        this.setState({
            connectionToDelete: connection,
        });
        this.showConfirmDeleteModal();
    }

    handleConnectionEdit = (connection) => {
        this.setState({
            connectionToUpdate: connection,
        });
        this.showUpdateConnectionModal();
    }

    executeDeleteConnection = async () => {
        try {
            console.log(`[deleteConnection] Invoking the back-end API to delete the IQ messenger connection with Id ${this.state.connectionToDelete.id}.`);

            await apiCalls.deleteIqMessengerConnection(this.props.authenticationContext, this.state.connectionToDelete.id);
            this.props.notificationContext.setNotification("Deleting connection.", `'${this.state.connectionToDelete.name}' successfully deleted.`, 'success');

            this.getConnections();
        }
        catch (err) {
            this.props.notificationContext.setNotification("Deleting connection.", `Error deleting '${this.state.connectionToDelete.name}'. ${err.toDetailedMessage()}`, 'danger');                  
        }

        this.hideConfirmDeleteModal();
    }

    executeAddOrUpdate = async (doneInfo) => {
        const connectionAction = doneInfo.isUpdate ? 'updating' : 'adding';
        const connectionActionDone = doneInfo.isUpdate ? 'updated' : 'added';

        try {
            console.log(`[executeAddOrUpdate] Invoking the back-end API ${connectionAction} the connection.`);

            if (doneInfo.isUpdate)
            {
                await apiCalls.updateIqMessengerConnection(this.props.authenticationContext, doneInfo.id, doneInfo.name, doneInfo.hostname, doneInfo.clientId, doneInfo.clientSecret);
            }
            else
            {
                await apiCalls.addIqMessengerConnection(this.props.authenticationContext, doneInfo.name, doneInfo.hostname, doneInfo.clientId, doneInfo.clientSecret);
            }

            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(connectionAction)} connection.`, `'${doneInfo.name}' successfully ${connectionActionDone}.`, 'success');
            this.getConnections();
        }
        catch (err) {
            this.props.notificationContext.setNotification(`${Misc.capitalizeFirstLetter(connectionAction)} connection.`, `Error ${connectionAction} '${doneInfo.name}'. ${err.toDetailedMessage()}`, 'danger');
        }
        finally {
            this.hideConnectionModal();
        }
    }

    shouldAddConnectionButtonBeEnabled = () => {
        if (this.state.connections.length >= maximumNumberOfConnections) {
            return false;
        }

        return true;
    }

    // ------------------------------
    // Rendering starts here...
    // ------------------------------
    render () {
        const headerRendered = <h1>Connection settings</h1> 

        if (this.state.isLoading) {
            return (
                <Container fluid>
                    {headerRendered}
                    {this.props.tenantContext.showWarningWhenDisabled()}
                    <p><em>Loading...</em></p>
                </Container>
            );
        }

        const confirmDeleteModalRendered = <>
            <Modal show={this.state.isConfirmDeleteModalOpen} onHide={this.hideConfirmDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete connection '{this.state.connectionToDelete.name}'?<br/>    
                </Modal.Body>
                <Modal.Footer>
                    <Button color="secondary" onClick={this.hideConfirmDeleteModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={this.executeDeleteConnection}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </>

        const connectionsRendered = this.state.connections.length > 0 ? 
            <IqMessengerConnections tenant={this.props.tenantContext} connections={this.state.connections} handleDelete={this.handleConnectionDelete} handleEdit={this.handleConnectionEdit}/> : 
            <Alert className="mt-3" variant='warning' transition={false}>
                No connections configured. Please add a connection which will be connected to the Zorgmessenger service.
            </Alert>;

        return (
            <Container fluid>
                {confirmDeleteModalRendered}
                <ModalConnectionAddOrUpdate isOpen={this.state.isConnectionModalOpen} isUpdate={this.state.isConnectionModalUpdate} tenant={this.props.tenantContext} connectionToUpdate={this.state.connectionToUpdate} onHide={this.hideConnectionModal} onAddOrUpdate={this.executeAddOrUpdate}/>
                {headerRendered}
                {this.props.tenantContext.showWarningWhenDisabled()}
                <hr/>
                <ButtonWithDisabledTooltip color="primary" disabled={!this.shouldAddConnectionButtonBeEnabled()} disabledTooltip="Maximum number of connections reached" onClick={this.showAddConnectionModal}>Add connection</ButtonWithDisabledTooltip>
                {connectionsRendered}
                <br/>
            </Container>
        );
    }
}

// Wrap the component so that multiple contexts are available via the props.
export const IqMessengerSettings = (props) => {
    const authenticationContext = useContext(AuthenticationContext);
    const notificationContext = useContext(NotificationContext);
    const tenantContext = useContext(TenantContext);
  
    return (
        <IqMessengerSettingsInternal authenticationContext={authenticationContext} notificationContext={notificationContext} tenantContext={tenantContext} {...props}/>
    )
}